<template>
  <div class="tab-content">
    <StepConfigurator
      :steps="stepsWithDynamicOptions"
      :is-locked="isConfigLocked"
      :is-config-invalid="isConfigInvalid"
      :allow-modify="false"
      @update-step="updateStep"
      @lock="lockConfig"
      @modify="unlockConfig"
    />

    <div v-if="isConfigLocked" class="counter-display">
      <span>完成标签绑定次数: {{ count }}</span>
      <button class="reset-counter-btn" @click="resetCounter">重置计数</button>
    </div>

    <div v-if="isConfigLocked" class="main-area">
      <div class="step-content">
        <div class="step">
          <Stepper
            :steps="enabledSteps"
            :current-step="displayCurrentStep"
            :is-failed="uiState.isFailed"
          />
          <div class="content">
            <div class="step-panel">
              <div v-if="uiState.isLoading" class="loader"></div>
              <label :class="{ 'fail-label': uiState.isFailed }">{{
                displayMessage
              }}</label>

              <StepForm
                v-if="currentStep < finalStepIndex"
                v-model="currentStepInputValue"
                :ui-state="uiState"
                @next="handleNext"
                @clear="clearValue"
                @reset="handleReset"
              />
              <div v-else-if="currentStep === finalStepIndex" class="step-form">
                <div class="button-list">
                  <button ref="nextButton" @click="handleReset">
                    下一个设备
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="uiState.isFailed && uiState.retryTime > 1"
            class="upload-exception"
          >
            <span v-if="uiState.isUploadException" class="click">
              已上报异常
            </span>
            <span v-else @click="reportException">多次重试失败？上报异常</span>
          </div>
        </div>
        <StepMessage v-if="isConfigLocked" ref="messageBox" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import HfImg from "@/assets/images/hf.jpg";
import UhfImg from "@/assets/images/uhf.jpg";
import Stepper from "@/components/MyStepper.vue";
import StepForm from "@/components/StepForm.vue";
import StepConfigurator from "@/components/StepConfigurator.vue";
import StepMessage from "@/components/StepMessage.vue";
import * as validators from "@/utils/validators";
import { useWorkflowCounter } from "@/composables/useWorkflowCounter";

const BINDING_WORKFLOW_CONFIG = {
  type: "binding",
  // lockable 为 false 时 INITIALIZE_WORKFLOW 直接锁定配置，进入页面即按默认配置开始
  lockable: false,
  configs: [
    {
      value: "",
      label: "scanShellCode",
      title: "扫描外壳二维码",
      desc: "请扫描外壳二维码",
      operation: "标签绑定",
      enabled: true,
      optional: false,
      prompt: "正在校验二维码...",
    },
    {
      value: "",
      label: "scanLeftTab",
      title: "扫描左侧标签",
      desc: "请扫描左侧标签",
      operation: "标签绑定",
      // 进入页面默认锁定：左侧超高频标签
      enabled: "uhf",
      optional: true,
      prompt: "正在校验...",
      configOptions: [
        { id: "disabled", label: "不启用" },
        { id: "hf", label: "高频标签", imageUrl: HfImg },
        { id: "uhf", label: "超高频标签", imageUrl: UhfImg },
      ],
    },
    {
      value: "",
      label: "scanRightTab",
      title: "扫描右侧标签",
      desc: "请扫描右侧标签",
      operation: "标签绑定",
      // 进入页面默认锁定：右侧不启用
      enabled: "disabled",
      optional: true,
      prompt: "正在校验...",
      configOptions: [
        { id: "disabled", label: "不启用" },
        { id: "hf", label: "高频标签", imageUrl: HfImg },
        { id: "uhf", label: "超高频标签", imageUrl: UhfImg },
      ],
    },
    {
      value: null,
      label: "finish",
      title: "完成",
      desc: "已完成绑定",
      operation: "标签绑定",
      enabled: true,
      optional: false,
      prompt: "正在保存数据...",
    },
  ],
};

const store = useStore();
const messageBox = ref(null);
const nextButton = ref(null);

// 防止重复输入的锁和防抖
const isProcessing = ref(false);
let inputDebounceTimer = null;
const DEBOUNCE_DELAY = 300; // 300ms防抖延迟

const stepConfigs = computed(() => store.state.workflow.stepConfigs);
const currentStep = computed(() => store.state.workflow.currentStep);
const uiState = computed(() => store.state.workflow.uiState);
const errResolution = computed(() => store.state.workflow.errorSolution);
const isConfigLocked = computed(() => store.state.workflow.isConfigLocked);
const currentStepConfig = computed(
  () => store.getters["workflow/currentStepConfig"]
);
const displayMessage = computed(() => store.getters["workflow/displayMessage"]);
const stepTip = computed(() => store.state.workflow.stepTip);

const enabledSteps = computed(() =>
  stepConfigs.value.filter((s) => s.enabled && s.enabled !== "disabled")
);
const finalStepIndex = computed(() => BINDING_WORKFLOW_CONFIG.configs.length);
const { count, increment, resetCounter } = useWorkflowCounter("binding");

const displayCurrentStep = computed(() => {
  const currentConfig = currentStepConfig.value;
  if (!currentConfig || enabledSteps.value.length === 0) return 1;
  const index = enabledSteps.value.findIndex(
    (s) => s.title === currentConfig.title
  );
  if (index === -1) {
    const originalIndex = stepConfigs.value.findIndex(
      (s) => s.title === currentConfig.title
    );
    for (let i = originalIndex - 1; i >= 0; i--) {
      const prevStepInEnabled = enabledSteps.value.findIndex(
        (s) => s.title === stepConfigs.value[i].title
      );
      if (prevStepInEnabled !== -1) {
        return prevStepInEnabled + 2;
      }
    }
    return 1;
  }
  return index + 1;
});

const currentStepInputValue = computed({
  get: () => currentStepConfig.value?.value || "",
  set: (value) =>
    store.commit("workflow/UPDATE_STEP_VALUE", {
      stepIndex: currentStep.value - 1,
      value,
    }),
});

const leftTabConfig = computed(() =>
  stepConfigs.value.find((s) => s.label === "scanLeftTab")
);
const rightTabConfig = computed(() =>
  stepConfigs.value.find((s) => s.label === "scanRightTab")
);

const isConfigInvalid = computed(() => {
  const leftEnabled = leftTabConfig.value?.enabled;
  const rightEnabled = rightTabConfig.value?.enabled;
  if (leftEnabled === null || rightEnabled === null) return true;
  if (leftEnabled === "disabled" && rightEnabled === "disabled") return true;
  return leftEnabled === "hf" && rightEnabled === "hf";
});

const stepsWithDynamicOptions = computed(() => {
  return stepConfigs.value.map((step) => {
    if (!step.optional) return step;
    const newOptions = step.configOptions.map((option) => {
      let isDisabled = false;
      let reason = "";
      if (
        step.label === "scanLeftTab" &&
        option.id === "hf" &&
        rightTabConfig.value?.enabled === "hf"
      ) {
        isDisabled = true;
        reason = "不允许双高频";
      }
      if (
        step.label === "scanRightTab" &&
        option.id === "hf" &&
        leftTabConfig.value?.enabled === "hf"
      ) {
        isDisabled = true;
        reason = "不允许双高频";
      }
      if (
        step.label === "scanLeftTab" &&
        option.id === "disabled" &&
        rightTabConfig.value?.enabled === "disabled"
      ) {
        isDisabled = true;
        reason = "不允许都不启用";
      }
      if (
        step.label === "scanRightTab" &&
        option.id === "disabled" &&
        leftTabConfig.value?.enabled === "disabled"
      ) {
        isDisabled = true;
        reason = "不允许都不启用";
      }
      return { ...option, disabled: isDisabled, disabledReason: reason };
    });
    return { ...step, configOptions: newOptions };
  });
});

const submitCurrentStep = async () => {
  if (isProcessing.value || uiState.value.isLoading) return;
  isProcessing.value = true;
  try {
    await store.dispatch("workflow/handleCurrentStep");
  } finally {
    if (uiState.value.isFailed || currentStep.value < finalStepIndex.value) {
      isProcessing.value = false;
    }
  }
};
const handleNext = () => submitCurrentStep();
const clearValue = () => {
  currentStepInputValue.value = "";
};
const initWorkflow = () =>
  store.dispatch("workflow/initializeWorkflow", BINDING_WORKFLOW_CONFIG);
const handleReset = () => {
  // 重置时清除处理锁和防抖定时器
  isProcessing.value = false;
  if (inputDebounceTimer) {
    clearTimeout(inputDebounceTimer);
    inputDebounceTimer = null;
  }

  store.dispatch("workflow/resetWorkflow", {
    ...BINDING_WORKFLOW_CONFIG,
    preserveConfig: true,
  });
};
const lockConfig = () => {
  if (!isConfigInvalid.value) {
    store.commit("workflow/LOCK_CONFIG");
  }
};
const unlockConfig = () => store.commit("workflow/UNLOCK_CONFIG");
const updateStep = ({ stepTitle, newConfig }) => {
  store.commit("workflow/UPDATE_STEP_CONFIG", { stepTitle, newConfig });
};
const reportException = () => store.dispatch("workflow/uploadException");

onMounted(initWorkflow);

watch(
  () => uiState.value.isFailed,
  (isFailed) => {
    // 失败时解除处理锁，允许重试
    if (isFailed) {
      isProcessing.value = false;
    }

    const failType = uiState.value.failType;
    const solution = errResolution.value[failType];
    const tip = stepTip.value[currentStepConfig.value.label];
    if (isFailed && solution) {
      messageBox.value?.show(solution);
    } else if (!isFailed && tip) {
      messageBox.value?.show(tip);
    } else {
      messageBox.value?.hide();
    }
  }
);
watch(
  [currentStep, isConfigLocked],
  async ([newCurrentStep], [oldCurrentStep]) => {
    await nextTick();

    // 步骤切换时清空输入框，防止残留数据
    if (
      newCurrentStep !== oldCurrentStep &&
      newCurrentStep < finalStepIndex.value
    ) {
      currentStepInputValue.value = "";
      // 解除处理锁
      isProcessing.value = false;
    }

    if (messageBox.value && !uiState.value.isFailed) {
      const tip = stepTip.value[currentStepConfig.value.label];
      tip ? messageBox.value.show(tip) : messageBox.value.hide();
    }

    const finalStepNum = BINDING_WORKFLOW_CONFIG.configs.length;

    if (
      newCurrentStep === finalStepNum &&
      newCurrentStep !== oldCurrentStep && // 确保是刚变化的
      !uiState.value.isFailed // 确保流程是成功的
    ) {
      increment();

      // 等待 DOM 更新，然后自动聚焦到按钮上
      await nextTick();
      if (nextButton.value) {
        nextButton.value.focus();
      }
    }
  },
  { immediate: true }
);
watch(
  currentStepInputValue,
  (newValue, oldValue) => {
    // 清除之前的防抖定时器
    if (inputDebounceTimer) {
      clearTimeout(inputDebounceTimer);
      inputDebounceTimer = null;
    }

    let value = newValue;
    if (!value) {
      isProcessing.value = false;
      return;
    }

    // 如果正在处理中，忽略新输入
    if (isProcessing.value) {
      console.warn("正在处理中，忽略重复输入:", value);
      return;
    }

    // 防抖处理：延迟执行验证
    inputDebounceTimer = setTimeout(() => {
      let isNext = false;

      if (currentStep.value === 1) {
        const { isValid } = validators.validateShellDeviceEUI(value);
        isNext = isValid;
      } else {
        let curTagType = currentStepConfig.value.enabled;
        if (!curTagType) return;
        curTagType = curTagType.toUpperCase();
        const tagType = validators.getTagType(value).toUpperCase();
        isNext = curTagType === tagType;
      }

      if (isNext) {
        submitCurrentStep();
      }
    }, DEBOUNCE_DELAY);
  },
  { immediate: true }
);
</script>

<style scoped>
.main-area {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 计数器样式 */
.counter-display {
  position: absolute;
  bottom: 0.5rem; /* 调整到 .content-area padding 内 */
  left: 1.5rem; /* 调整到 .content-area padding 内 */
  background: rgba(240, 245, 255, 0.9);
  padding: 5px 10px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10;
  font-size: 0.9rem;
}
.reset-counter-btn {
  background: #f56c6c;
  color: white;
  border: none;
  padding: 2px 5px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.8rem;
}
.reset-counter-btn:hover {
  background: #f89898;
}
</style>
